/**
 * Unified API Client
 *
 * Single source of truth for API base URL, authentication headers,
 * and response handling. All service modules should import from here
 * instead of defining their own.
 *
 * @module services/apiClient
 */

// Production: /api proxied by Firebase Hosting to Cloud Run
// Development: localhost or env var
export const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_VALUE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000');

/**
 * Map of HTTP status codes and error types to user-friendly messages.
 * Used by handleResponse and handleApiError for consistent UX.
 */
const FRIENDLY_ERROR_MESSAGES = {
  // Auth errors
  401: 'Your session has expired. Please sign in again.',
  403: 'Your session has expired. Please sign in again.',
  // Rate limiting
  429: 'Too many requests. Please wait a moment and try again.',
  // Client errors
  400: 'The request was invalid. Please check your input and try again.',
  404: 'The requested resource was not found.',
  408: 'The request timed out. Please try again.',
  409: 'A conflict occurred. Please refresh and try again.',
  413: 'The upload is too large. Please reduce the file size.',
  422: 'The data provided is invalid. Please check and try again.',
  // Server errors
  500: 'Something went wrong on our end. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again in a moment.',
  503: 'The service is temporarily unavailable. Please try again in a moment.',
  504: 'The server took too long to respond. Please try again.',
};

/**
 * Detect whether an error is a network/connectivity error.
 * @param {Error} error
 * @returns {boolean}
 */
const isNetworkError = (error) => {
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) return true;
  if (error.name === 'TypeError' && error.message.includes('NetworkError')) return true;
  if (error.message?.includes('ECONNREFUSED')) return true;
  if (error.message?.includes('ENOTFOUND')) return true;
  if (error.message?.includes('ETIMEDOUT')) return true;
  if (error.message?.includes('ERR_NETWORK')) return true;
  if (error.name === 'AbortError') return true;
  return false;
};

/**
 * Get a user-friendly message for a given HTTP status code.
 * Falls back to a generic message for unknown codes.
 * @param {number} status - HTTP status code
 * @param {string} [serverMessage] - Optional server-provided message
 * @returns {string} User-friendly error message
 */
export const getFriendlyErrorMessage = (status, serverMessage) => {
  // Use mapped message for known status codes
  if (FRIENDLY_ERROR_MESSAGES[status]) {
    return FRIENDLY_ERROR_MESSAGES[status];
  }
  // Generic fallback for other 4xx
  if (status >= 400 && status < 500) {
    return serverMessage || 'Something went wrong with your request. Please try again.';
  }
  // Generic fallback for other 5xx
  if (status >= 500) {
    return 'Something went wrong on our end. Please try again later.';
  }
  return serverMessage || 'An unexpected error occurred. Please try again.';
};

/**
 * Get required authentication headers.
 * @returns {Promise<Object>} Headers with Authorization and Content-Type
 * @throws {Error} If user is not authenticated
 */
export const getAuthHeaders = async () => {
  const { auth } = await import('../firebase');
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Authentication required. Please sign in.');
  }
  const token = await user.getIdToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  // Include organization context for multi-tenant RLS isolation
  try {
    const orgId = localStorage.getItem('portfolistic_current_org');
    if (orgId) {
      headers['X-Organization-ID'] = orgId;
    }
  } catch {
    // Private browsing or localStorage unavailable
  }

  return headers;
};

/**
 * Get optional authentication headers (for public endpoints).
 * @returns {Promise<Object>} Headers with optional Authorization
 */
export const getOptionalAuthHeaders = async () => {
  try {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      return { 'Content-Type': 'application/json' };
    }
    const token = await user.getIdToken();
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    // Include organization context for multi-tenant RLS isolation
    try {
      const orgId = localStorage.getItem('portfolistic_current_org');
      if (orgId) {
        headers['X-Organization-ID'] = orgId;
      }
    } catch {
      // Private browsing
    }

    return headers;
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

/**
 * Custom API error class that carries HTTP status and rate limit info.
 */
export class ApiError extends Error {
  /**
   * @param {string} message - User-friendly error message
   * @param {number} status - HTTP status code
   * @param {string} [rawMessage] - Raw server message for debugging
   * @param {Object} [rateLimitInfo] - Rate limit headers if applicable
   */
  constructor(message, status, rawMessage, rateLimitInfo) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.rawMessage = rawMessage || message;
    this.rateLimitInfo = rateLimitInfo || null;
  }

  /** @returns {boolean} True if this is a rate limit error */
  get isRateLimit() {
    return this.status === 429 || (this.status === 403 && this.rateLimitInfo?.remaining === 0);
  }

  /** @returns {boolean} True if this is an auth error */
  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  /** @returns {boolean} True if this is a server error */
  get isServerError() {
    return this.status >= 500;
  }
}

/**
 * Extract rate limit information from response headers.
 * Supports both GitHub-style (X-RateLimit-*) and standard headers.
 * @param {Response} response - Fetch response
 * @returns {Object|null} Rate limit info or null
 */
export const extractRateLimitInfo = (response) => {
  const remaining = response.headers.get('X-RateLimit-Remaining');
  const limit = response.headers.get('X-RateLimit-Limit');
  const reset = response.headers.get('X-RateLimit-Reset');
  const retryAfter = response.headers.get('Retry-After');

  if (remaining !== null || limit !== null || reset !== null || retryAfter !== null) {
    const resetTimestamp = reset ? parseInt(reset, 10) * 1000 : null; // Convert to ms
    const retryAfterMs = retryAfter
      ? (parseInt(retryAfter, 10) > 1000000000 // If it looks like a timestamp
        ? parseInt(retryAfter, 10) * 1000
        : Date.now() + parseInt(retryAfter, 10) * 1000) // Seconds from now
      : null;

    return {
      remaining: remaining !== null ? parseInt(remaining, 10) : null,
      limit: limit !== null ? parseInt(limit, 10) : null,
      resetAt: resetTimestamp || retryAfterMs || null,
    };
  }

  return null;
};

/**
 * Handle API response - throws ApiError on non-OK responses with
 * user-friendly messages. Preserves rate limit info for callers.
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} Parsed JSON
 * @throws {ApiError} On non-OK responses
 */
export const handleResponse = async (response) => {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    // Support both new { error: { message } } and legacy { detail } formats
    const serverMessage = data?.error?.message || data?.detail || `HTTP ${response.status}`;
    const friendlyMessage = getFriendlyErrorMessage(response.status, serverMessage);

    // Check for rate limit info in headers
    const rateLimitInfo = extractRateLimitInfo(response);

    // Special handling for 403 with rate limit exhaustion
    const isRateLimitExhausted = response.status === 403 && (
      rateLimitInfo?.remaining === 0 ||
      serverMessage.toLowerCase().includes('rate limit')
    );

    if (isRateLimitExhausted) {
      throw new ApiError(
        'API rate limit exceeded. Please wait before making more requests.',
        403,
        serverMessage,
        rateLimitInfo
      );
    }

    throw new ApiError(friendlyMessage, response.status, serverMessage, rateLimitInfo);
  }
  return response.json();
};

/**
 * Check if API is available.
 * @returns {boolean}
 */
export const isApiAvailable = () => {
  if (process.env.REACT_APP_API_AVAILABLE === 'true') return true;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
  if (API_BASE && !API_BASE.includes('localhost') && !API_BASE.includes('127.0.0.1')) return true;
  return true;
};

/**
 * Handle API errors consistently with user-friendly messages.
 * @param {Error} error
 * @param {string} context - Description of what was being attempted
 * @param {Object} fallback - Fallback return value
 * @returns {Object}
 */
export const handleApiError = (error, context, fallback = {}) => {
  // Network/connectivity errors
  if (isNetworkError(error)) {
    return { ...fallback, error: 'Unable to connect to server. Please check your connection and try again.' };
  }

  // ApiError instances already have friendly messages
  if (error instanceof ApiError) {
    return { ...fallback, error: error.message, status: error.status, rateLimitInfo: error.rateLimitInfo };
  }

  // Legacy TypeError from fetch
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    return { ...fallback, error: 'Unable to connect to server. Please check your connection and try again.' };
  }

  return { ...fallback, error: error.message || 'An unexpected error occurred. Please try again.' };
};

const apiClient = {
  API_BASE,
  getAuthHeaders,
  getOptionalAuthHeaders,
  handleResponse,
  isApiAvailable,
  handleApiError,
  getFriendlyErrorMessage,
  extractRateLimitInfo,
  ApiError,
};

export default apiClient;
